// frontend/src/components/onboarding/org-destination-card.tsx
// Org-level webhook destination (GET/PUT/DELETE /organizations/:orgId/destination).
// Mounted on the SaaS Channels page (admin gated by the route/sidebar).
// No role check here — the parent page already guarantees admin context.
import { useEffect, useRef, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { Eye, EyeOff, Info, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldError } from '@/components/form/field-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { optionalHttpsUrlSchema } from '@/lib/form/schemas';
import { midEllipsis } from '@/lib/mid-ellipsis';
import { CopyButton } from './copy-button';
import {
  useClearOrgDestination,
  useOrgDestinationConfig,
  useRotateOrgDestinationHmac,
  useSaveOrgDestination,
} from './queries';

interface OrgDestinationCardProps {
  orgId: string;
  /** Called after the org destination URL is successfully saved (PUT). */
  onSaved?: () => void;
  /** Called after the org destination is successfully cleared (DELETE). */
  onCleared?: () => void;
}

function validateOptionalHttpsUrl(value: string): string | undefined {
  const parsed = optionalHttpsUrlSchema.safeParse(value.trim());
  return parsed.success
    ? undefined
    : (parsed.error.issues[0]?.message ?? 'Enter a valid URL');
}

/**
 * Shows the org-level webhook destination URL + verify token and lets an admin
 * save (PUT) or clear (DELETE) it.
 *
 * The Save/Clear row only appears once the inputs differ from the saved value
 * (dirty state). A successful save syncs the saved snapshot to the new values,
 * so the row disappears and a success toast confirms it. Clear only shows when
 * a destination is already saved. Save errors surface inline (400 validation
 * messages shown as-is; networking failures get a generic fallback). Button
 * labels are Title Case.
 */
export function OrgDestinationCard({ orgId, onSaved, onCleared }: OrgDestinationCardProps) {
  const destinationQuery = useOrgDestinationConfig(orgId);
  const saveDestination = useSaveOrgDestination(orgId);
  const clearDestination = useClearOrgDestination(orgId);
  const rotateHmac = useRotateOrgDestinationHmac(orgId);
  const [rotateHmacDialogOpen, setRotateHmacDialogOpen] = useState(false);
  const [hmacRevealed, setHmacRevealed] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const savedDestinationSnapshotRef = useRef<string | null>(null);
  const destination = destinationQuery.data;
  const hmacSecret = destination?.hmacSecret ?? null;
  const savedUrl = destination?.webhookUrl ?? '';
  const savedVerifyToken = destination?.verifyToken ?? '';
  const hasSaved = savedUrl !== '';

  const form = useForm({
    defaultValues: {
      url: '',
      verifyToken: '',
    },
    onSubmit: async ({ value }) => {
      const nextUrl = value.url.trim();
      const nextToken = value.verifyToken.trim();
      if (nextUrl === savedUrl && nextToken === savedVerifyToken) return;
      setSaveError(null);
      try {
        await saveDestination.mutateAsync({ webhookUrl: nextUrl, verifyToken: nextToken });
        // Re-fetch to pick up the HMAC the server mints on first save.
        await destinationQuery.refetch();
        toast.success('Inbound destination saved');
        onSaved?.();
      } catch (err) {
        setSaveError(
          err && typeof err === 'object' && 'userMessage' in err
            ? ((err as { userMessage?: string }).userMessage ?? 'Failed to save destination')
            : 'Failed to save destination',
        );
      }
    },
  });

  useEffect(() => {
    if (!destination) return;
    const snapshot = `${destination.webhookUrl ?? ''}\u0000${destination.verifyToken ?? ''}`;
    if (savedDestinationSnapshotRef.current === snapshot) return;
    savedDestinationSnapshotRef.current = snapshot;
    form.setFieldValue('url', destination.webhookUrl ?? '');
    form.setFieldValue('verifyToken', destination.verifyToken ?? '');
  }, [destination, form]);

  async function handleClear() {
    setSaveError(null);
    try {
      await clearDestination.mutateAsync();
      toast.success('Inbound destination cleared');
      onCleared?.();
    } catch (err) {
      setSaveError(
        err && typeof err === 'object' && 'userMessage' in err
          ? ((err as { userMessage?: string }).userMessage ?? 'Failed to clear destination')
          : 'Failed to clear destination',
      );
    }
  }

  async function handleRotateHmac() {
    setSaveError(null);
    try {
      await rotateHmac.mutateAsync();
      setRotateHmacDialogOpen(false);
      // Auto-reveal the freshly rotated secret so the admin can copy it
      // straight into their endpoint — mirrors AccessTokenField's rotate.
      setHmacRevealed(true);
      toast.success('HMAC secret rotated');
    } catch (err) {
      setSaveError(
        err && typeof err === 'object' && 'userMessage' in err
          ? ((err as { userMessage?: string }).userMessage ?? 'Failed to rotate HMAC secret')
          : 'Failed to rotate HMAC secret',
      );
    }
  }

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Inbound Destination</CardTitle>
        <CardDescription>
          The organization-wide webhook endpoint. New onboarding links inherit it,
          and you can apply it to existing channels. Leave it empty to keep channels
          on HookMyApp CLI by default.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit();
          }}
          className="space-y-4"
        >
          <form.Field
            name="url"
            validators={{
              onChange: ({ value }) => validateOptionalHttpsUrl(value),
              onSubmit: ({ value }) => validateOptionalHttpsUrl(value),
            }}
          >
            {(field) => {
              const hasError = field.state.meta.errors.length > 0;
              return (
                <div className="space-y-2">
                  <Label htmlFor="dest-url">Webhook URL</Label>
                  <Input
                    id="dest-url"
                    value={field.state.value}
                    onChange={(e) => {
                      if (saveError) setSaveError(null);
                      field.handleChange(e.target.value);
                    }}
                    onBlur={field.handleBlur}
                    placeholder="https://hooks.example.com/receive"
                    aria-invalid={hasError || undefined}
                    aria-describedby={hasError ? 'dest-url-error' : undefined}
                  />
                  <FieldError id="dest-url-error" errors={field.state.meta.errors} />
                </div>
              );
            }}
          </form.Field>
          <form.Field name="verifyToken">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="dest-verify">Verify Token</Label>
                <Input
                  id="dest-verify"
                  value={field.state.value}
                  onChange={(e) => {
                    if (saveError) setSaveError(null);
                    field.handleChange(e.target.value);
                  }}
                  onBlur={field.handleBlur}
                  placeholder="your-verify-token"
                />
              </div>
            )}
          </form.Field>
          {/* HMAC — read-only signing secret (copy + rotate), visible whenever
              a destination is saved. DISTINCT from the Verify Token above: we
              generate it, you only read it to verify our signature. Applying the
              destination to channels copies this secret onto each one. */}
          {hmacSecret && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1" htmlFor="dest-hmac">
                HMAC
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button variant="ghost" size="icon-xs" aria-label="What is the HMAC secret">
                        <Info className="size-3.5" />
                      </Button>
                    }
                  />
                  <TooltipContent className="max-w-xs">
                    We sign every forwarded webhook with the header
                    X-HookMyApp-Signature-256 = sha256=HMAC-SHA256(this secret,
                    raw request body). Recompute it on your endpoint to confirm
                    the webhook is from us. Applying the destination to channels
                    copies this secret onto each one. This is not the verify token.
                  </TooltipContent>
                </Tooltip>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="dest-hmac"
                  readOnly
                  value={hmacRevealed ? hmacSecret : midEllipsis(hmacSecret, 4, 4)}
                  className="flex-1 font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label={hmacRevealed ? 'Hide HMAC secret' : 'Reveal HMAC secret'}
                  title={hmacRevealed ? 'Hide' : 'Reveal'}
                  onClick={() => setHmacRevealed((r) => !r)}
                >
                  {hmacRevealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                </Button>
                <CopyButton value={hmacSecret} label="HMAC Secret" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={rotateHmac.isPending}
                  aria-label="Rotate HMAC Secret"
                  onClick={() => setRotateHmacDialogOpen(true)}
                >
                  {rotateHmac.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <RotateCcw className="size-3.5" />
                  )}
                  Rotate
                </Button>
              </div>
            </div>
          )}
          {saveError && (
            <p role="alert" className="text-sm text-destructive">
              {saveError}
            </p>
          )}
          <form.Subscribe
            selector={(state) => {
              const dirty =
                state.values.url !== savedUrl ||
                state.values.verifyToken !== savedVerifyToken;
              const invalid = validateOptionalHttpsUrl(state.values.url) !== undefined;
              return { dirty, invalid };
            }}
          >
            {({ dirty, invalid }) =>
              dirty ? (
                <div className="flex justify-end gap-2">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={saveDestination.isPending || clearDestination.isPending || invalid}
                  >
                    {saveDestination.isPending && <Loader2 className="size-3.5 animate-spin" />}
                    Save
                  </Button>
                  {hasSaved && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={saveDestination.isPending || clearDestination.isPending}
                      onClick={() => void handleClear()}
                    >
                      {clearDestination.isPending && <Loader2 className="size-3.5 animate-spin" />}
                      Clear
                    </Button>
                  )}
                </div>
              ) : null
            }
          </form.Subscribe>
        </form>
      </CardContent>
    </Card>

    <AlertDialog
      open={rotateHmacDialogOpen}
      onOpenChange={(open) => {
        if (!rotateHmac.isPending && !open) setRotateHmacDialogOpen(false);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Rotate HMAC secret?</AlertDialogTitle>
          <AlertDialogDescription>
            A new org HMAC signing secret is generated. It reaches your channels
            only on the next bulk-apply, and endpoints verifying the current
            secret will fail until you copy the new one in. The verify token is
            not affected.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={rotateHmac.isPending}>Keep Current</AlertDialogCancel>
          <AlertDialogAction
            disabled={rotateHmac.isPending}
            onClick={(e) => {
              e.preventDefault();
              void handleRotateHmac();
            }}
          >
            Rotate Secret
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
